// === IMPORTS ===
import React, { useState, useEffect, useRef } from 'react';
import { AIAgent, AgentAvatarSpriteSet, SpriteDirection, SpriteSlotKey } from '../types';
import {
  SPRITE_16_SLOTS, DIRECTION_ORDER, SPRITE_PRESETS, generateFull16SpriteSet,
  getFrameKey, SpriteSlotDefinition,
} from '../utils/agentSpriteEngine';
import {
  X, Upload, Sparkles, Trash2, Play, Pause, Check, FolderUp, Sliders,
  Layers, Image as ImageIcon, Compass, ArrowRightLeft, Info,
} from 'lucide-react';
import '../styles/twin-modals-shared.css';
import '../styles/twin-icons-shared.css';
import './AgentAvatarSpriteModal.css';

interface AgentAvatarSpriteModalProps {
  agent: AIAgent;
  isOpen: boolean;
  onClose: () => void;
  onSaveSpriteSet: (agentId: string, spriteSet: AgentAvatarSpriteSet, customImagePreview?: string) => void;
}

// === COMPONENTE: EDITOR DE SPRITE SET DE 16 CUADROS ===
export const AgentAvatarSpriteModal: React.FC<AgentAvatarSpriteModalProps> = ({
  agent, isOpen, onClose, onSaveSpriteSet,
}) => {
  const [spriteSet, setSpriteSet] = useState<AgentAvatarSpriteSet>(() => {
    if (agent.spriteSet && Object.keys(agent.spriteSet).length > 0) return { ...agent.spriteSet };
    return generateFull16SpriteSet(agent.avatarShirtColor, agent.hairColor);
  });

  const [previewDirection, setPreviewDirection] = useState<SpriteDirection>('front');
  const [previewWalking, setPreviewWalking] = useState<boolean>(true);
  const [previewSpeed, setPreviewSpeed] = useState<number>(150);
  const [animFrameIndex, setAnimFrameIndex] = useState<number>(0);
  const [selectedDirectionTab, setSelectedDirectionTab] = useState<'all' | SpriteDirection>('all');
  const [urlInputSlot, setUrlInputSlot] = useState<SpriteSlotKey | null>(null);
  const [tempUrl, setTempUrl] = useState<string>('');

  const bulkFileInputRef = useRef<HTMLInputElement>(null);
  const singleFileInputRef = useRef<HTMLInputElement>(null);
  const [activeSlotTarget, setActiveSlotTarget] = useState<SpriteSlotKey | null>(null);

  useEffect(() => {
    if (agent.spriteSet && Object.keys(agent.spriteSet).length > 0) {
      setSpriteSet({ ...agent.spriteSet });
    } else {
      setSpriteSet(generateFull16SpriteSet(agent.avatarShirtColor, agent.hairColor));
    }
  }, [agent.id, agent.spriteSet]);

  useEffect(() => {
    if (!previewWalking) { setAnimFrameIndex(0); return; }
    const interval = setInterval(() => { setAnimFrameIndex((prev) => (prev + 1) % 4); }, previewSpeed);
    return () => clearInterval(interval);
  }, [previewWalking, previewSpeed]);

  if (!isOpen) return null;

  const currentPreviewKey = getFrameKey(previewDirection, previewWalking, animFrameIndex);
  const currentPreviewImage = spriteSet[currentPreviewKey] || '';
  const configuredCount = SPRITE_16_SLOTS.filter((s) => Boolean(spriteSet[s.key])).length;

  const handleApplyPreset = (presetId: string) => {
    const preset = SPRITE_PRESETS.find((p) => p.id === presetId);
    if (!preset) return;
    setSpriteSet(generateFull16SpriteSet(preset.shirtColor, preset.hairColor, undefined, preset.pantsColor));
  };

  const handleGenerateProcedural = () => {
    setSpriteSet(generateFull16SpriteSet(agent.avatarShirtColor || undefined, agent.hairColor || undefined));
  };

  const handleClearAll = () => setSpriteSet({});

  const flipImageHorizontal = (dataUrl: string): Promise<string> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const c = document.createElement('canvas');
        c.width = img.width || 34;
        c.height = img.height || 38;
        const ctx = c.getContext('2d');
        if (!ctx) { resolve(dataUrl); return; }
        ctx.translate(c.width, 0);
        ctx.scale(-1, 1);
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(img, 0, 0);
        resolve(c.toDataURL('image/png'));
      };
      img.onerror = () => resolve(dataUrl);
      img.src = dataUrl;
    });
  };

  const handleAutoMirrorHorizontal = async () => {
    const updated = { ...spriteSet };
    const seKeys: SpriteSlotKey[] = ['front_idle', 'front_walk1', 'front_walk2', 'front_walk3'];
    const swKeys: SpriteSlotKey[] = ['left_idle', 'left_walk1', 'left_walk2', 'left_walk3'];
    for (let i = 0; i < 4; i++) {
      const src = updated[seKeys[i]];
      if (src) updated[swKeys[i]] = await flipImageHorizontal(src);
    }
    const neKeys: SpriteSlotKey[] = ['right_idle', 'right_walk1', 'right_walk2', 'right_walk3'];
    const nwKeys: SpriteSlotKey[] = ['back_idle', 'back_walk1', 'back_walk2', 'back_walk3'];
    for (let i = 0; i < 4; i++) {
      const src = updated[neKeys[i]];
      if (src) updated[nwKeys[i]] = await flipImageHorizontal(src);
    }
    setSpriteSet(updated);
  };

  const handleSingleSlotUpload = (key: SpriteSlotKey, file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUri = e.target?.result as string;
      if (dataUri) setSpriteSet((prev) => ({ ...prev, [key]: dataUri }));
    };
    reader.readAsDataURL(file);
  };

  const handleBulkUpload = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const fileArray = Array.from(files).slice(0, 16);
    fileArray.forEach((file, index) => {
      const slot = SPRITE_16_SLOTS[index];
      if (slot) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const dataUri = e.target?.result as string;
          if (dataUri) setSpriteSet((prev) => ({ ...prev, [slot.key]: dataUri }));
        };
        reader.readAsDataURL(file);
      }
    });
  };

  const handleSetUrl = (key: SpriteSlotKey) => {
    if (tempUrl.trim()) {
      setSpriteSet((prev) => ({ ...prev, [key]: tempUrl.trim() }));
      setUrlInputSlot(null);
      setTempUrl('');
    }
  };

  const handleSave = () => {
    const fallbackThumb = spriteSet.front_idle || Object.values(spriteSet)[0] || '';
    onSaveSpriteSet(agent.id, spriteSet, fallbackThumb);
    onClose();
  };

  const filteredSlots = selectedDirectionTab === 'all'
    ? SPRITE_16_SLOTS
    : SPRITE_16_SLOTS.filter((s) => s.direction === selectedDirectionTab);

  const activeDirectionMeta = DIRECTION_ORDER.find((d) => d.id === previewDirection);

  // === RENDER ===
  return (
    <div className="twin-modal-overlay">
      <div className="twin-modal twin-spritemodal">
        <input
          type="file" ref={singleFileInputRef} accept="image/png,image/jpeg,image/webp,image/gif" className="twin-hidden"
          onChange={(e) => {
            if (e.target.files?.[0] && activeSlotTarget) {
              handleSingleSlotUpload(activeSlotTarget, e.target.files[0]);
              e.target.value = '';
            }
          }}
        />
        <input
          type="file" ref={bulkFileInputRef} multiple accept="image/png,image/jpeg,image/webp,image/gif" className="twin-hidden"
          onChange={(e) => { handleBulkUpload(e.target.files); e.target.value = ''; }}
        />

        {/* === HEADER === */}
        <div className="twin-spritemodal-header">
          <div className="twin-spritemodal-header-left">
            <div className="twin-spritemodal-icon-badge" style={{ backgroundColor: agent.avatarShirtColor || '#3b82f6' }}>🎭</div>
            <div>
              <h3 className="twin-spritemodal-title">16-Sprite Set for Isometric 3/4 Movement</h3>
              <p className="twin-spritemodal-subtitle">
                Agent: <span className="twin-color-cyan">{agent.nickname || agent.name}</span> — 4 Isometric Diagonals × 4 Walk Cycle States
              </p>
            </div>
          </div>
          <div className="twin-spritemodal-header-right">
            <span className={`twin-spritemodal-count-badge ${configuredCount === 16 ? 'twin-spritemodal-count-badge--complete' : 'twin-spritemodal-count-badge--incomplete'}`}>
              {configuredCount} / 16 Assigned Frames
            </span>
            <button onClick={onClose} className="twin-modal-close-btn twin-spritemodal-close-btn">
              <X className="twin-icon-md" />
            </button>
          </div>
        </div>

        {/* === CUERPO === */}
        <div className="twin-spritemodal-body">
          {/* === BANNER DE GUÍA ISOMÉTRICA === */}
          <div className="twin-spritemodal-guide-banner">
            <div className="twin-spritemodal-guide-icon"><Compass className="twin-icon-md" /></div>
            <div className="twin-spritemodal-guide-text">
              <div className="twin-spritemodal-guide-title-row">
                <p className="twin-spritemodal-guide-title">📐 Sizing & Proportions: 2 Cubes Height (64 px)</p>
                <span className="twin-spritemodal-guide-badge">Base: 64 × 32 px | Height: 64 px</span>
              </div>
              <p className="twin-spritemodal-guide-desc">
                Each isometric floor tile measures <strong>64 × 32 px</strong>. The vertical unit height of a standard cube is <strong>32 px</strong>. Thus, the avatar's body is proportioned to exactly <strong>2 cubes high (64 px)</strong> with a proportional width of <strong>56 px</strong> in <strong>3/4 diagonal perspective</strong> (Southeast ↘️, Southwest ↙️, Northwest ↖️, Northeast ↗️).
              </p>
            </div>
          </div>

          {/* === ESCENARIO DE PREVIEW EN VIVO + CONTROLES === */}
          <div className="twin-spritemodal-preview-grid">
            {/* === CAJA DEL PREVIEW ISOMÉTRICO === */}
            <div className="twin-spritemodal-stage-col">
              <span className="twin-spritemodal-stage-label"><Layers className="twin-icon-xs twin-color-cyan" /> Isometric Tile Simulator</span>

              <div className="twin-spritemodal-stage">
                <svg className="twin-spritemodal-stage-svg" viewBox="0 0 160 160">
                  <polygon points="80,45 145,85 80,125 15,85" fill="#1e293b" stroke="#475569" strokeWidth="1.5" />
                  <polygon points="80,60 125,85 80,110 35,85" fill="#0f172a" stroke="#334155" strokeWidth="1" strokeDasharray="2 2" />
                  <line x1="80" y1="85" x2="145" y2="120" stroke="#0284c7" strokeWidth="1" strokeDasharray="3 3" />
                  <line x1="80" y1="85" x2="15" y2="120" stroke="#8b5cf6" strokeWidth="1" strokeDasharray="3 3" />
                  <text x="120" y="130" fill="#38bdf8" fontSize="8" fontFamily="monospace">↘️ +Col</text>
                  <text x="18" y="130" fill="#a78bfa" fontSize="8" fontFamily="monospace">↙️ +Row</text>
                </svg>

                <div className="twin-spritemodal-stage-character">
                  {currentPreviewImage ? (
                    <img src={currentPreviewImage} alt="Preview" className="twin-spritemodal-stage-img" style={{ imageRendering: 'pixelated' }} />
                  ) : (
                    <div className="twin-spritemodal-stage-empty">
                      <ImageIcon className="twin-icon-lg" />
                      No frame loaded
                    </div>
                  )}
                </div>

                <div className="twin-spritemodal-frame-badge">{currentPreviewKey}</div>
                <div className="twin-spritemodal-direction-badge">
                  <span>{activeDirectionMeta?.icon}</span>
                  <span>{activeDirectionMeta?.isoName}</span>
                </div>
              </div>

              <div className="twin-spritemodal-status-readout">
                <p className="twin-spritemodal-status-text">{previewWalking ? '🚶 Walking in 3/4 Diagonal' : '🧍 Idle in Isometric Pose'}</p>
                <p className="twin-spritemodal-status-sub">{activeDirectionMeta?.gridDescription}</p>
              </div>
            </div>

            {/* === CONTROLES DE ANIMACIÓN Y ACCIONES === */}
            <div className="twin-spritemodal-controls-col">
              <div className="twin-spritemodal-control-group">
                <div className="twin-spritemodal-row-between">
                  <span className="twin-spritemodal-control-label">Isometric Diagonal (3/4):</span>
                  <div className="twin-spritemodal-direction-btns">
                    {DIRECTION_ORDER.map((dir) => (
                      <button key={dir.id} type="button" onClick={() => setPreviewDirection(dir.id)}
                        className={`twin-spritemodal-dir-btn ${previewDirection === dir.id ? 'twin-spritemodal-dir-btn--active' : ''}`}>
                        <span>{dir.icon}</span>
                        <span>{dir.label.split(' ')[0]}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="twin-spritemodal-row-between twin-spritemodal-mode-row">
                  <span className="twin-spritemodal-control-label">Animation Mode:</span>
                  <div className="twin-spritemodal-mode-btns">
                    <button type="button" onClick={() => setPreviewWalking(false)} className={`twin-spritemodal-mode-btn ${!previewWalking ? 'twin-spritemodal-mode-btn--idle-active' : ''}`}>
                      <Pause className="twin-icon-xs" /> Idle Pose
                    </button>
                    <button type="button" onClick={() => setPreviewWalking(true)} className={`twin-spritemodal-mode-btn ${previewWalking ? 'twin-spritemodal-mode-btn--walk-active' : ''}`}>
                      <Play className="twin-icon-xs" /> Walk Cycle
                    </button>
                  </div>
                </div>

                <div className="twin-spritemodal-speed-row">
                  <span className="twin-spritemodal-speed-label"><Sliders className="twin-icon-xs twin-color-cyan" /> Walk Cycle Speed:</span>
                  <input type="range" min={80} max={300} step={10} value={previewSpeed} onChange={(e) => setPreviewSpeed(Number(e.target.value))} className="twin-range twin-range--sprite" />
                  <span className="twin-spritemodal-speed-value">{previewSpeed} ms</span>
                </div>
              </div>

              <div className="twin-spritemodal-actions-row">
                <button type="button" onClick={() => bulkFileInputRef.current?.click()} className="twin-spritemodal-action-btn twin-spritemodal-action-btn--cyan">
                  <FolderUp className="twin-icon-sm" /> Upload 16-File Pack
                </button>
                <button type="button" title="Auto-generates opposite diagonals by horizontal mirroring (SE to SW and NE to NW)" onClick={handleAutoMirrorHorizontal} className="twin-spritemodal-action-btn twin-spritemodal-action-btn--purple">
                  <ArrowRightLeft className="twin-icon-sm" /> Isometric Mirror (↔️)
                </button>
                <button type="button" onClick={handleGenerateProcedural} className="twin-spritemodal-action-btn twin-spritemodal-action-btn--indigo">
                  <Sparkles className="twin-icon-sm" /> Procedural Pixel Art (3/4)
                </button>

                <div className="twin-spritemodal-presets-row">
                  <span className="twin-spritemodal-presets-label">Presets:</span>
                  {SPRITE_PRESETS.slice(0, 4).map((p) => (
                    <button key={p.id} type="button" onClick={() => handleApplyPreset(p.id)} title={p.description} className="twin-spritemodal-preset-btn">
                      <span>{p.icon}</span>
                      <span>{p.name.split('/')[0]}</span>
                    </button>
                  ))}
                  <button type="button" onClick={handleClearAll} title="Clear all frames" className="twin-spritemodal-clear-btn">
                    <Trash2 className="twin-icon-sm" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* === PESTAÑAS DE FILTRO POR DIRECCIÓN === */}
          <div className="twin-spritemodal-filter-bar">
            <div className="twin-spritemodal-filter-tabs">
              <button type="button" onClick={() => setSelectedDirectionTab('all')} className={`twin-spritemodal-filter-tab ${selectedDirectionTab === 'all' ? 'twin-spritemodal-filter-tab--active' : ''}`}>
                All 16 Slots
              </button>
              {DIRECTION_ORDER.map((dir) => (
                <button key={dir.id} type="button" onClick={() => setSelectedDirectionTab(dir.id)} className={`twin-spritemodal-filter-tab ${selectedDirectionTab === dir.id ? 'twin-spritemodal-filter-tab--active' : ''}`}>
                  <span>{dir.icon}</span>
                  <span>{dir.label}</span>
                </button>
              ))}
            </div>
            <p className="twin-spritemodal-filter-hint">
              <Info className="twin-icon-sm twin-color-cyan" />
              Upload PNGs with transparent backgrounds aligned to the corresponding diagonal angle.
            </p>
          </div>

          {/* === GRILLA DE 16 SLOTS === */}
          <div className="twin-spritemodal-slots-grid">
            {filteredSlots.map((slot: SpriteSlotDefinition) => {
              const currentImg = spriteSet[slot.key];
              const isSelectedForUrl = urlInputSlot === slot.key;

              return (
                <div key={slot.key} className={`twin-spritemodal-slot ${currentImg ? '' : 'twin-spritemodal-slot--empty'}`}>
                  <div className="twin-spritemodal-slot-header">
                    <div>
                      <div className="twin-spritemodal-slot-number-row">
                        <span className="twin-spritemodal-slot-number">{slot.slotNumber}</span>
                        <span className="twin-spritemodal-slot-label">{slot.shortLabel}</span>
                      </div>
                      <p className="twin-spritemodal-slot-frame-label">{slot.frameLabel}</p>
                      <p className="twin-spritemodal-slot-axis">{slot.gridAxisLabel}</p>
                    </div>
                    <span className="twin-spritemodal-slot-icon">{slot.directionIcon}</span>
                  </div>

                  <div
                    onClick={() => { setActiveSlotTarget(slot.key); singleFileInputRef.current?.click(); }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files?.[0]) handleSingleSlotUpload(slot.key, e.dataTransfer.files[0]); }}
                    className="twin-spritemodal-slot-thumb"
                  >
                    {currentImg ? (
                      <img src={currentImg} alt={slot.shortLabel} className="twin-spritemodal-slot-img" style={{ imageRendering: 'pixelated' }} />
                    ) : (
                      <div className="twin-spritemodal-slot-upload-placeholder">
                        <Upload className="twin-icon-md" />
                        <span>Upload Image</span>
                      </div>
                    )}

                    {currentImg && (
                      <button type="button" title="Delete this frame" onClick={(e) => { e.stopPropagation(); setSpriteSet((prev) => ({ ...prev, [slot.key]: undefined })); }} className="twin-spritemodal-slot-delete-btn">
                        <Trash2 className="twin-icon-xs" />
                      </button>
                    )}
                  </div>

                  <div className="twin-spritemodal-slot-footer">
                    <p className="twin-spritemodal-slot-desc">{slot.description}</p>

                    {isSelectedForUrl ? (
                      <div className="twin-spritemodal-url-row">
                        <input type="text" placeholder="https://..." value={tempUrl} onChange={(e) => setTempUrl(e.target.value)} className="twin-spritemodal-url-input" />
                        <button type="button" onClick={() => handleSetUrl(slot.key)} className="twin-spritemodal-url-confirm"><Check className="twin-icon-xs" /></button>
                        <button type="button" onClick={() => setUrlInputSlot(null)} className="twin-spritemodal-url-cancel"><X className="twin-icon-xs" /></button>
                      </div>
                    ) : (
                      <div className="twin-spritemodal-slot-links">
                        <button type="button" onClick={() => { setActiveSlotTarget(slot.key); singleFileInputRef.current?.click(); }} className="twin-spritemodal-slot-link">
                          <Upload className="twin-icon-xs" /> Upload PNG
                        </button>
                        <button type="button" onClick={() => { setUrlInputSlot(slot.key); setTempUrl(currentImg || ''); }} className="twin-spritemodal-slot-link twin-spritemodal-slot-link--muted">
                          Paste URL
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* === FOOTER === */}
        <div className="twin-spritemodal-footer">
          <div className="twin-spritemodal-footer-hint">Changes are applied in real-time to the agent rendering in the isometric canvas and live simulation.</div>
          <div className="twin-spritemodal-footer-actions">
            <button type="button" onClick={onClose} className="twin-link-btn twin-spritemodal-cancel-btn">Cancel</button>
            <button type="button" onClick={handleSave} className="twin-spritemodal-save-btn">
              <Check className="twin-icon-sm" /> Save 16-Sprite Set
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};